import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import WhatsAppChat from 'components/ui/WhatsAppChat';
import CallButton from 'components/ui/CallButton';
import Button from 'components/ui/Button';

import CategoryFilter from './components/CategoryFilter';

import ImageLightbox from './components/ImageLightbox';
import SearchBar from './components/SearchBar';
import MasonryGrid from './components/MasonryGrid';
import LoadingSpinner from './components/LoadingSpinner';

const PhotoGallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mock gallery data
  const categories = [
    { id: 'all', name: 'All Photos' },
    { id: 'accommodations', name: 'Accommodations' },
    { id: 'activities', name: 'Activities' },
    { id: 'dining', name: 'Dining' },
    { id: 'nature', name: 'Nature' },
    { id: 'facilities', name: 'Facilities' }
  ];

  const galleryImages = [
    {
      id: 1,
      src: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop",
      alt: "Deluxe Forest Villa exterior view",
      title: "Deluxe Forest Villa",
      location: "Forest Area",
      category: "Accommodations",
      description: "Spacious villa nestled among towering trees with modern amenities and panoramic forest views."
    },
    {
      id: 2,
      src: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?w=800&h=1000&fit=crop",
      alt: "River rafting adventure on Kali River",
      title: "White Water Rafting",
      location: "Kali River",
      category: "Activities",
      description: "Thrilling white water rafting experience through the pristine waters of Kali River."
    },
    {
      id: 3,
      src: "https://images.pixabay.com/photo/2016/11/29/05/45/astronomy-1867616_1280.jpg?w=800&h=600&fit=crop",
      alt: "Luxury tent under starry night sky",
      title: "Luxury Camping Experience",
      location: "Riverside Camping Area",
      category: "Accommodations",
      description: "Premium camping experience with comfortable tents under the mesmerizing night sky."
    },
    {
      id: 4,
      src: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=900&fit=crop",
      alt: "Traditional South Indian breakfast spread",
      title: "Authentic Local Cuisine",
      location: "Main Restaurant",
      category: "Dining",
      description: "Delicious traditional Karnataka cuisine prepared with fresh local ingredients."
    },
    {
      id: 5,
      src: "https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?w=800&h=1200&fit=crop",
      alt: "Dense forest canopy with morning mist",
      title: "Misty Forest Morning",
      location: "Dandeli Wildlife Sanctuary",
      category: "Nature",
      description: "Breathtaking morning mist rolling through the dense Western Ghats forest canopy."
    },
    {
      id: 6,
      src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
      alt: "Jungle safari jeep in wildlife sanctuary",
      title: "Wildlife Safari Adventure",
      location: "Dandeli Wildlife Sanctuary",
      category: "Activities",
      description: "Exciting jungle safari to spot tigers, leopards, elephants and diverse bird species."
    },
    {
      id: 7,
      src: "https://images.pixabay.com/photo/2017/08/06/12/06/people-2591874_1280.jpg?w=800&h=600&fit=crop",
      alt: "Swimming pool with forest backdrop",
      title: "Infinity Pool",
      location: "Main Resort Area",
      category: "Facilities",
      description: "Stunning infinity pool overlooking the forest with crystal clear mountain spring water."
    },
    {
      id: 8,
      src: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=1000&fit=crop",
      alt: "Kayaking on calm river waters",
      title: "Peaceful Kayaking",
      location: "Kali River",
      category: "Activities",
      description: "Serene kayaking experience through calm river sections surrounded by lush greenery."
    },
    {
      id: 9,
      src: "https://images.pexels.com/photos/1579739/pexels-photo-1579739.jpeg?w=800&h=600&fit=crop",
      alt: "Tree house accommodation among branches",
      title: "Elevated Tree House",
      location: "Canopy Level",
      category: "Accommodations",
      description: "Unique tree house experience elevated 20 feet above ground with panoramic canopy views."
    },
    {
      id: 10,
      src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=800&fit=crop",
      alt: "Campfire gathering with guests",
      title: "Evening Campfire",
      location: "Central Gathering Area",
      category: "Activities",
      description: "Cozy evening campfire sessions with storytelling, music, and stargazing activities."
    },
    {
      id: 11,
      src: "https://images.pixabay.com/photo/2018/01/14/23/12/nature-3082832_1280.jpg?w=800&h=1200&fit=crop",
      alt: "Waterfall cascading through rocks",
      title: "Syntheri Rocks Waterfall",
      location: "Syntheri Rocks",
      category: "Nature",
      description: "Magnificent waterfall cascading through ancient rock formations creating natural pools."
    },
    {
      id: 12,
      src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop",
      alt: "Spa treatment room with natural elements",
      title: "Wellness Spa",
      location: "Spa & Wellness Center",
      category: "Facilities",
      description: "Rejuvenating spa treatments using traditional Ayurvedic methods and natural ingredients."
    },
    {
      id: 13,
      src: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?w=800&h=600&fit=crop",
      alt: "River view cottage balcony",
      title: "River View Cottage",
      location: "Riverside",
      category: "Accommodations",
      description: "Charming cottage with private balcony offering stunning views of the flowing Kali River."
    },
    {
      id: 14,
      src: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=900&fit=crop",
      alt: "Bird watching tower in forest",
      title: "Bird Watching Experience",
      location: "Forest Observatory",
      category: "Activities",
      description: "Dedicated bird watching tower for spotting over 200 species of resident and migratory birds."
    },
    {
      id: 15,
      src: "https://images.pixabay.com/photo/2017/08/06/12/06/people-2591874_1280.jpg?w=800&h=1000&fit=crop",
      alt: "Outdoor dining setup under trees",
      title: "Forest Dining Experience",
      location: "Outdoor Dining Area",
      category: "Dining",
      description: "Unique outdoor dining experience under ancient trees with candlelit ambiance."
    }
  ];

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Filter images based on category and search term
  const filteredImages = useMemo(() => {
    let filtered = galleryImages;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered?.filter(image => 
        image?.category?.toLowerCase() === activeCategory?.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(image =>
        image?.title?.toLowerCase()?.includes(searchLower) ||
        image?.location?.toLowerCase()?.includes(searchLower) ||
        image?.category?.toLowerCase()?.includes(searchLower) ||
        image?.description?.toLowerCase()?.includes(searchLower)
      );
    }

    return filtered;
  }, [activeCategory, searchTerm]);

  const handleImageClick = (image, index) => {
    const actualIndex = galleryImages?.findIndex(img => img?.id === image?.id);
    setSelectedImageIndex(actualIndex);
    setIsLightboxOpen(true);
  };

  const handleNextImage = () => {
    if (selectedImageIndex < galleryImages?.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
      <Helmet>
        <title>Photo Gallery - Dandeli Adventure Resorts | Stunning Resort & Nature Photography</title>
        <meta name="description" content="Explore our stunning photo gallery showcasing luxury accommodations, thrilling activities, and breathtaking nature at Dandeli Adventure Resorts. View high-resolution images of our facilities and surroundings." />
        <meta name="keywords" content="Dandeli resort photos, accommodation gallery, adventure activities images, nature photography, Karnataka tourism, Western Ghats, resort facilities" />
        <meta property="og:title" content="Photo Gallery - Dandeli Adventure Resorts" />
        <meta property="og:description" content="Browse through our beautiful collection of resort photos, activities, and natural landscapes at Dandeli Adventure Resorts." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/photo-gallery" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 py-16">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <div className="max-w-3xl mx-auto">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6">
                  Photo Gallery
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                  Immerse yourself in the natural beauty and luxury of Dandeli Adventure Resorts through our stunning collection of photographs showcasing accommodations, activities, and breathtaking landscapes.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => window.location.href = '/booking-engine'}
                  >
                    Book Your Stay
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Mountain"
                    iconPosition="left"
                    onClick={() => window.location.href = '/activities-experiences'}
                  >
                    Explore Activities
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Gallery Controls */}
          <section className="py-12 bg-surface">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              {/* Search Bar */}
              <SearchBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={handleClearSearch}
              />

              {/* Category Filter */}
              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />

              {/* Results Count */}
              <div className="text-center mb-8">
                <p className="text-muted-foreground">
                  {searchTerm ? (
                    <>Showing {filteredImages?.length} result{filteredImages?.length !== 1 ? 's' : ''} for "{searchTerm}"</>
                  ) : (
                    <>Showing {filteredImages?.length} photo{filteredImages?.length !== 1 ? 's' : ''} in {categories?.find(cat => cat?.id === activeCategory)?.name}</>
                  )}
                </p>
              </div>
            </div>
          </section>

          {/* Gallery Grid */}
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <MasonryGrid
                  images={filteredImages}
                  onImageClick={handleImageClick}
                />
              )}
            </div>
          </section>

          {/* Call to Action */}
          {!isLoading && filteredImages?.length > 0 && (
            <section className="py-16 bg-gradient-to-r from-primary to-secondary">
              <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                  Ready to Experience This Paradise?
                </h2>
                <p className="text-xl text-white/90 mb-8">
                  Book your stay at Dandeli Adventure Resorts and create your own unforgettable memories in nature's embrace.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => window.location.href = '/booking-engine'}
                    className="bg-white text-primary hover:bg-white/90"
                  >
                    Book Now
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => window.location.href = 'tel:+919876543210'}
                    className="border-white text-white hover:bg-white hover:text-primary"
                  >
                    Call Us
                  </Button>
                </div>
              </div>
            </section>
          )}
        </main>

        {/* Image Lightbox */}
        <ImageLightbox
          isOpen={isLightboxOpen}
          images={galleryImages}
          currentIndex={selectedImageIndex}
          onClose={handleCloseLightbox}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
        />

        {/* Floating Action Buttons */}
        <WhatsAppChat />
        <CallButton />
      </div>
    </>
  );
};

export default PhotoGallery;